import { normalizeDealAgent } from '../utils.js';

/**
 * computeDcAffiliates.js
 *
 * Чистая (side-effect-free) функция агрегации для колонки Affiliates в Daily Cap.
 *
 * Гибридная логика (owner-решение 2026-06-14):
 *   - Если для (date, agent) ЕСТЬ запись в daily-cap-aff-caps → Affiliates = Σ affs[].cap
 *   - Если записи НЕТ → fallback: Σ deal.cap по manageAff из crgDeals (старая CRG-логика)
 *   - Brands: всегда из madeSale (CRG), без изменений
 *
 * Порядок слоёв (всё считается здесь, overrides накладываются выше в DailyCap/index.jsx):
 *   1. CRG-сделки → capMap[date][agent].{affiliates, brands}
 *   2. Для date+agent где есть aff-caps запись → перезаписать affiliates суммой настроек
 *   3. Overrides (DailyCap/index.jsx ovMap) — НЕ касаются этой функции
 *
 * @param {Array} crgDeals  — массив CRG-сделок { date, cap, manageAff, madeSale }
 * @param {Array} affCaps   — массив настроек { id, date, agent, affs: [{affId, cap}] }
 * @param {Function} normalizeAgent — нормализатор имён агентов (из utils.js)
 * @returns {Array} entries — [{ id, date, agent, affiliates: string, brands: string }]
 */
export function computeDcAffiliates(crgDeals, affCaps, normalizeAgent) {
  const capMap = {}; // { date: { agentNorm: { affiliates, brands } } }

  // ── PASS 1: CRG → заполняем affiliates (manageAff) и brands (madeSale) ──
  (crgDeals || []).forEach(deal => {
    const date = deal.date;
    if (!date) return;
    const capVal = parseInt(deal.cap) || 0;
    if (capVal === 0) return;

    if (!capMap[date]) capMap[date] = {};

    const mAff = (deal.manageAff || '').trim();
    if (mAff) {
      // Нормализуем так же как делал оригинальный useMemo
      const norm = normalizeDealAgent(mAff);
      if (!capMap[date][norm]) capMap[date][norm] = { affiliates: 0, brands: 0 };
      capMap[date][norm].affiliates += capVal;
    }

    const mSale = (deal.madeSale || '').trim();
    if (mSale) {
      const norm = normalizeDealAgent(mSale);
      if (!capMap[date][norm]) capMap[date][norm] = { affiliates: 0, brands: 0 };
      capMap[date][norm].brands += capVal;
    }
  });

  // ── PASS 2: aff-caps настройки → перезаписываем affiliates где есть запись ──
  // Гибрид per-(date, agent):
  //   - Есть запись → Affiliates = Σ affs[].cap (настройки берут приоритет над CRG)
  //   - Нет записи → Affiliates остаётся из PASS 1 (CRG-fallback)
  //
  // ВАЖНО: Brands из настроек НЕ меняется — только Affiliates.
  //
  // Структура aff-caps: { id: "YYYY-MM-DD|Agent", date, agent, affs: [{affId, cap}] }
  // "Перезаписываем" означает: сбрасываем CRG-значение и ставим SUM настроек.
  // Если в настройке affs=[]} (пустой список) → affiliates = 0 (оператор явно очистил).
  const affCapsApplied = new Set(); // "date|normAgent" — где уже применены настройки

  (affCaps || []).forEach(record => {
    const { date, agent, affs } = record;
    if (!date || !agent) return;

    const norm = normalizeAgent(agent);
    if (!norm) return;

    const key = `${date}|${norm}`;
    if (affCapsApplied.has(key)) {
      // Дубль записи для того же date+agent → суммируем (defensive)
      const existing = capMap[date]?.[norm];
      if (existing) {
        const extra = (affs || []).reduce((s, a) => s + (parseInt(a.cap) || 0), 0);
        existing.affiliates += extra;
      }
      return;
    }
    affCapsApplied.add(key);

    if (!capMap[date]) capMap[date] = {};
    if (!capMap[date][norm]) capMap[date][norm] = { affiliates: 0, brands: 0 };

    // Перезаписываем affiliates суммой настроек (даже если 0 — оператор явно задал)
    const total = (affs || []).reduce((s, a) => s + (parseInt(a.cap) || 0), 0);
    capMap[date][norm].affiliates = total;
  });

  // ── Flatten capMap → entries array ──
  const result = [];
  Object.keys(capMap).forEach(date => {
    Object.keys(capMap[date]).forEach(agent => {
      const { affiliates, brands } = capMap[date][agent];
      // stable id — идентично прежнему useMemo, React.memo работает без лишних re-renders
      result.push({
        id: `dc-${date}-${agent}`,
        date,
        agent,
        affiliates: String(affiliates),
        brands: String(brands),
      });
    });
  });
  return result;
}
